<template>
  <ol :class="$style.root">
    <li
      v-for="(step, stepIndex) in steps"
      :key="stepIndex"
      :class="[
        $style.item,
        stepIndex === currentIndex && $style.itemActive,
        stepIndex < currentIndex && $style.itemComplete,
        stepIndex > currentIndex && $style.itemPending,
        stepIndex < steps.length - 1 && $style.itemHasLine,
      ]"
    >
      <span :class="$style.number">{{ stepIndex + 1 }}</span>
      <span :class="$style.label">
        <span class="kiosk-visible-landscape-screen-only">{{ step.long }}</span>
        <span class="kiosk-visible-portrait-screen-only">{{ step.short }}</span>
      </span>
    </li>
  </ol>
</template>

<script setup lang="ts">
export interface UiStep {
  long: string;
  short: string;
}

interface Props {
  steps: UiStep[];
  currentIndex: number;
}

defineProps<Props>();
</script>

<style module>
.root {
  --ui-step-progress-counter-size: var(--kiosk-step-progress-counter-size);
  --ui-step-progress-line-height: 2px;

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  align-items: start;
  list-style: none;
  padding: 0 1rem var(--kiosk-step-progress-padding-bottom);
  margin: 0 auto;
  width: max-content;
  max-width: 100%;
}

.item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  min-width: 0;
  padding-inline: 0.5rem;
}

.itemHasLine::after {
  content: '';
  position: absolute;
  top: calc(var(--ui-step-progress-counter-size) / 2 - var(--ui-step-progress-line-height) / 2);
  left: calc(50% + var(--ui-step-progress-counter-size) / 2);
  width: calc(100% - var(--ui-step-progress-counter-size));
  height: var(--ui-step-progress-line-height);
  z-index: 1;
  background: var(--Kiosk--font_color);
}

.itemHasLine.itemActive::after,
.itemHasLine.itemPending::after {
  background: rgba(var(--Kiosk--font_color-rgb), 0.3);
}

.itemPending .number {
  background: transparent;
  color: rgba(var(--Kiosk--font_color-rgb), 0.5);
  border-color: rgba(var(--Kiosk--font_color-rgb), 0.5);
}

.itemPending .label {
  color: rgba(var(--Kiosk--font_color-rgb), 0.5);
}

.itemComplete .number,
.itemActive .number {
  background: var(--ui-step-progress-number-bg, var(--Kiosk--font_color));
  color: var(--ui-step-progress-number-color, var(--Kiosk--background_color));
  border-color: var(--ui-step-progress-number-bg, var(--Kiosk--font_color));
}

.number {
  width: var(--ui-step-progress-counter-size);
  height: var(--ui-step-progress-counter-size);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: calc(var(--ui-step-progress-counter-size) * 0.55);
  font-weight: 600;
  border: 2px solid;
  box-sizing: border-box;
  position: relative;
  z-index: 2;
  flex-shrink: 0;
}

.label {
  width: 100%;
  margin-top: var(--kiosk-step-progress-label-margin-top);
  font-size: var(--kiosk-step-progress-label-font-size);
  text-transform: var(--Kiosk--font_capitalize, uppercase);
  letter-spacing: 0.03em;
  text-align: center;
  overflow-wrap: anywhere;
}

</style>
